import React, { useMemo } from 'react';
import cx from 'classnames';
import { useSpring, animated } from 'react-spring';

import StepContext, { StepContextObject } from './StepContext';
import { Transition } from '~/App/Components/Typeform';

export type AnimatedChildProps = {
  visible: boolean;
  index: number;
  completed: boolean;
  onClick: () => void;
  onNextStep: () => void;
  onPreviousStep: () => void;
  onScrollToStep: (index: number) => void;
  onFocus: () => void;
  children: React.ReactNode;
  className?: string;
  transition: Transition;
};

const AnimatedChild = React.forwardRef(
  (
    {
      index,
      visible,
      children,
      className,
      completed,
      onClick,
      onNextStep,
      onPreviousStep,
      onScrollToStep,
      onFocus,
      transition,
    }: AnimatedChildProps,
    ref
  ) => {
    const transitionStyle = useSpring(
      useMemo(() => {
        switch (transition) {
          case Transition.FadeIn:
            return {
              opacity: visible ? 1 : 0.2,
            };
          default:
            return {
              opacity: 1,
            };
        }
      }, [visible, transition])
    );

    const stepContext: StepContextObject = {
      index: index,
      stepRef: ref,
      isComplete: completed,
      isCurrent: visible,
      nextStep: onNextStep,
      previousStep: onPreviousStep,
      scrollToStep: onScrollToStep,
      focus: onFocus,
    };

    return (
      <StepContext.Provider value={stepContext} key={index}>
        <animated.div
          className={cx(
            className,
            'outline-none h-full w-full flex justify-center'
          )}
          ref={ref as React.RefObject<HTMLDivElement>}
          disabled={!visible}
          style={transitionStyle}
          onClick={onClick}
          tabIndex={-1}
        >
          {children}
        </animated.div>
      </StepContext.Provider>
    );
  }
);

export default AnimatedChild;
